import React, { useEffect, useRef, useState } from 'react';
import './BackgroundAudio.css';

// Use local music file from public folder
// INSTRUCTIONS: Place your music file in the "public" folder and name it "background-music.mp3"
// Supported formats: .mp3, .wav, .ogg
// Example: public/background-music.mp3
const audioSources = [
  '/background-music.mp3',  // Your local music file - PLACE THIS IN THE public FOLDER
  '/background-music.wav',  // Alternative: if you have .wav file
  '/background-music.ogg'   // Alternative: if you have .ogg file
];

function BackgroundAudio() {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const [audioSource, setAudioSource] = useState(0);

  useEffect(() => {
    const audio = audioRef.current;
    
    if (audio) {
      // Set audio properties for smooth playback
      audio.volume = 1.0; // Set to 100% for maximum audibility
      audio.loop = true; // Loop the audio continuously
      audio.muted = false; // Ensure audio is not muted
      
      // Force unmute in case browser muted it
      if (audio.muted) {
        audio.muted = false;
      }
      
      // Verify audio properties
      console.log('Audio initialized - Volume:', audio.volume, 'Muted:', audio.muted, 'Loop:', audio.loop, 'Source:', audioSources[audioSource]);
      
      // Set initial source
      audio.src = audioSources[audioSource];
      audio.load(); // Explicitly load the audio
      
      // Handle when audio is loaded and ready
      const handleCanPlay = async () => {
        // Only try autoplay if user hasn't interacted yet
        if (!hasUserInteracted && audio.paused) {
          try {
            await audio.play();
            setIsPlaying(true);
          } catch (error) {
            // Autoplay blocked - this is normal, wait for user interaction
            setIsPlaying(false);
          }
        }
      };

      // Also try when enough data is loaded
      const handleLoadedData = async () => {
        // Only try autoplay if user hasn't interacted yet
        if (!hasUserInteracted && audio.paused && audio.readyState >= 2) {
          try {
            await audio.play();
            setIsPlaying(true);
          } catch (error) {
            // Autoplay blocked - this is normal
            setIsPlaying(false);
          }
        }
      };

      // Listen for user interaction to enable audio - SIMPLIFIED
      const enableAudio = async () => {
        if (audio.paused) {
          setHasUserInteracted(true);
          try {
            // Ensure audio source is set
            if (!audio.src || audio.src === '') {
              audio.src = audioSources[audioSource];
              audio.load();
            }
            
            // Ensure audio is not muted and volume is set to maximum
            audio.muted = false;
            audio.volume = 1.0; // 100% volume
            
            // Double check it's not muted
            if (audio.muted) {
              console.warn('Audio was muted, forcing unmute...');
              audio.muted = false;
            }
            
            console.log('Before play - Volume:', audio.volume, 'Muted:', audio.muted, 'ReadyState:', audio.readyState);
            
            // Wait a tiny bit for audio to be ready
            if (audio.readyState < 2) {
              await new Promise(resolve => {
                const checkReady = () => {
                  if (audio.readyState >= 2) {
                    audio.removeEventListener('canplay', checkReady);
                    resolve();
                  }
                };
                audio.addEventListener('canplay', checkReady);
                setTimeout(resolve, 2000); // Max 2 second wait
              });
            }
            
            // Play immediately
            await audio.play();
            setIsPlaying(true);
            console.log('🎵 Music is now playing!');
          } catch (error) {
            console.log('Error playing audio:', error);
            // If play fails, try next source
            const nextSource = (audioSource + 1) % audioSources.length;
            if (nextSource !== audioSource) {
              setAudioSource(nextSource);
            }
          }
        }
      };

      // Add event listeners for user interaction
      const events = ['click', 'touchstart', 'keydown', 'mousedown'];
      events.forEach(event => {
        document.addEventListener(event, enableAudio, { once: true, passive: true });
      });

      // Handle audio events
      const handlePlay = () => {
        console.log('✅ Audio is playing - You should hear music now!');
        console.log('Audio details - Volume:', audio.volume, 'Muted:', audio.muted, 'CurrentTime:', audio.currentTime, 'Duration:', audio.duration);
        
        // Test if audio context is working
        try {
          const audioContext = new (window.AudioContext || window.webkitAudioContext)();
          console.log('Audio Context State:', audioContext.state);
          if (audioContext.state === 'suspended') {
            console.warn('⚠️ Audio Context is suspended - user interaction may be needed');
          }
        } catch (e) {
          console.warn('Could not create Audio Context:', e);
        }
        
        setIsPlaying(true);
      };
      
      const handlePause = () => {
        console.log('⏸️ Audio is paused');
        setIsPlaying(false);
      };
      
      const handleEnded = () => {
        console.log('⏹️ Audio ended');
        setIsPlaying(false);
      };
      
      const handleTimeUpdate = () => {
        // This confirms audio is actually playing - log every 5 seconds
        const currentSecond = Math.floor(audio.currentTime);
        if (audio.currentTime > 0 && !audio.paused && currentSecond % 5 === 0 && currentSecond > 0) {
          console.log('🎵 Music is playing! Time:', audio.currentTime.toFixed(1), 's, Volume:', audio.volume, 'Muted:', audio.muted, 'Duration:', audio.duration?.toFixed(1) || 'unknown');
          
          // Check if audio is actually outputting sound
          if (audio.muted || audio.volume === 0) {
            console.error('⚠️ WARNING: Audio is muted or volume is 0!');
            audio.muted = false;
            audio.volume = 1.0;
          }
        }
      };
      
      const handleError = (e) => {
        console.log('❌ Audio error, trying next source...', e);
        const nextSource = (audioSource + 1) % audioSources.length;
        
        if (nextSource !== audioSource) {
          console.log(`🔄 Trying audio source ${nextSource + 1}/${audioSources.length}: ${audioSources[nextSource]}`);
          // Update state to trigger useEffect with new source
          setAudioSource(nextSource);
        } else {
          console.error('❌ All audio sources failed');
          setIsPlaying(false);
        }
      };

      audio.addEventListener('canplay', handleCanPlay);
      audio.addEventListener('canplaythrough', handleCanPlay);
      audio.addEventListener('play', handlePlay);
      audio.addEventListener('pause', handlePause);
      audio.addEventListener('ended', handleEnded);
      audio.addEventListener('error', handleError);
      audio.addEventListener('loadeddata', handleLoadedData);
      audio.addEventListener('timeupdate', handleTimeUpdate);

      // Pause audio when tab becomes hidden or window is closed
      const handleVisibilityChange = () => {
        if (document.hidden) {
          // Tab is hidden or user switched to another tab
          if (audio && !audio.paused) {
            audio.pause();
            setIsPlaying(false);
            console.log('⏸️ Audio paused - tab is hidden');
          }
        }
      };

      // Pause audio when window is about to close
      const handleBeforeUnload = () => {
        if (audio && !audio.paused) {
          audio.pause();
          audio.currentTime = 0;
          console.log('⏸️ Audio stopped - window closing');
        }
      };

      // Add visibility change listener
      document.addEventListener('visibilitychange', handleVisibilityChange);
      window.addEventListener('beforeunload', handleBeforeUnload);
      // Also handle pagehide for better browser compatibility
      window.addEventListener('pagehide', handleBeforeUnload);

      // Try to play after audio loads (only if user hasn't interacted)
      const timer1 = setTimeout(() => {
        if (!hasUserInteracted) {
          handleCanPlay();
        }
      }, 1000);

      return () => {
        clearTimeout(timer1);
        events.forEach(event => {
          document.removeEventListener(event, enableAudio);
        });
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        window.removeEventListener('beforeunload', handleBeforeUnload);
        window.removeEventListener('pagehide', handleBeforeUnload);
        if (audio) {
          audio.removeEventListener('canplay', handleCanPlay);
          audio.removeEventListener('canplaythrough', handleCanPlay);
          audio.removeEventListener('play', handlePlay);
          audio.removeEventListener('pause', handlePause);
          audio.removeEventListener('ended', handleEnded);
          audio.removeEventListener('error', handleError);
          audio.removeEventListener('loadeddata', handleLoadedData);
          audio.removeEventListener('timeupdate', handleTimeUpdate);
          audio.pause();
          audio.currentTime = 0;
        }
      };
    }
  }, [audioSource, hasUserInteracted]);

  const toggleAudio = async () => {
    const audio = audioRef.current;
    if (audio) {
      if (audio.paused) {
        setHasUserInteracted(true);
        try {
          // Ensure source is set
          if (!audio.src || audio.src === '') {
            audio.src = audioSources[audioSource];
            audio.load();
          }
          
          // Ensure audio is not muted and volume is set to maximum
          audio.muted = false;
          audio.volume = 1.0; // 100% volume
          
          // Double check it's not muted
          if (audio.muted) {
            console.warn('Audio was muted, forcing unmute...');
            audio.muted = false;
          }
          
          console.log('Before play - Volume:', audio.volume, 'Muted:', audio.muted, 'ReadyState:', audio.readyState);
          
          // Wait for audio to be ready
          if (audio.readyState < 2) {
            await new Promise(resolve => {
              const checkReady = () => {
                if (audio.readyState >= 2) {
                  audio.removeEventListener('canplay', checkReady);
                  resolve();
                }
              };
              audio.addEventListener('canplay', checkReady);
              setTimeout(resolve, 2000);
            });
          }
          
          await audio.play();
          setIsPlaying(true);
          console.log('🎵 Music is now playing!');
        } catch (error) {
          console.log('Error playing audio:', error);
          // Try next source if current fails
          const nextSource = (audioSource + 1) % audioSources.length;
          if (nextSource !== audioSource) {
            setAudioSource(nextSource);
          }
        }
      } else {
        audio.pause();
        setIsPlaying(false);
        console.log('Music paused');
      }
    }
  };

  return (
    <>
      <audio
        ref={audioRef}
        src={audioSources[audioSource]}
        preload="auto"
      />
      <button
        className="audio-control-button"
        onClick={toggleAudio}
        aria-label={isPlaying ? 'Mute background music' : 'Play background music'}
        title={isPlaying ? 'Click to mute' : 'Click to play music'}
      >
        {isPlaying ? (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M3 9V15H7L12 20V4L7 9H3ZM16.5 12C16.5 10.23 15.45 8.71 14.34 7.66L15.75 6.25C17.33 7.83 18.5 9.83 18.5 12C18.5 14.17 17.33 16.17 15.75 17.75L14.34 16.34C15.45 15.29 16.5 13.77 16.5 12ZM19 12C19 9.07 17.5 6.5 15.25 4.75L13.84 6.16C15.5 7.5 16.5 9.64 16.5 12C16.5 14.36 15.5 16.5 13.84 17.84L15.25 19.25C17.5 17.5 19 14.93 19 12Z" fill="currentColor"/>
          </svg>
        ) : (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M16.5 12C16.5 10.23 15.45 8.71 14.34 7.66L15.75 6.25C17.33 7.83 18.5 9.83 18.5 12C18.5 14.17 17.33 16.17 15.75 17.75L14.34 16.34C15.45 15.29 16.5 13.77 16.5 12ZM19 12C19 9.07 17.5 6.5 15.25 4.75L13.84 6.16C15.5 7.5 16.5 9.64 16.5 12C16.5 14.36 15.5 16.5 13.84 17.84L15.25 19.25C17.5 17.5 19 14.93 19 12ZM3 9V15H7L12 20V4L7 9H3Z" fill="currentColor"/>
          </svg>
        )}
      </button>
    </>
  );
}

export default BackgroundAudio;
